/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Button, Box, TextField, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BreadCrumbs } from '../breadcrumbs';
import toast, { useToasterStore } from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction'; // for selectable option
import { days } from '../../_mock';
import { LoadingButton } from '@mui/lab';

import {
  getUserDetails,
  formatDateTimeSlots,
  formattedSlotsForCalendar,
  accessUserCalendar,
  isDateInCurrentWeek,
  getHourlyFormat,
} from '../../utils';
import {
  userQueryGetTimeSlots,
  useMutationModifyTimeSlots,
  useMutationSendInviteNotifications,
  useQueryGetSentInvites,
} from '../../hooks/react-query/useData';

import useGoogleSignIn from '../../hooks/google-signin/useGoogleSignin';


import { TimeSlotsDialog } from '../dialog';

const TOAST_LIMIT = 2;

// Color palette for slots
const SLOT_COLORS = [
  { bg: '#8E71AD', border: '#8E71AD' },
  { bg: '#6B8E23', border: '#6B8E23' },
  { bg: '#4682B4', border: '#4682B4' },
  { bg: '#CD5C5C', border: '#CD5C5C' },
  { bg: '#20B2AA', border: '#20B2AA' },
  { bg: '#DA70D6', border: '#DA70D6' },
  { bg: '#FF6347', border: '#FF6347' },
  { bg: '#32CD32', border: '#32CD32' },
  { bg: '#FF8C00', border: '#FF8C00' },
  { bg: '#4169E1', border: '#4169E1' },
  { bg: '#DC143C', border: '#DC143C' },
  { bg: '#00CED1', border: '#00CED1' },
];

// Function to get a random color from the palette
const getRandomColor = () => {
  return SLOT_COLORS[Math.floor(Math.random() * SLOT_COLORS.length)];
};

const AvailabilityCalendar = ({ isEdifier }) => {

  const calendarRef = useRef(null);

  const { toasts } = useToasterStore();

  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const [currentUserGoogleEvents, setCurrentUserGoogleEvents] = useState([]);

  let [validatedDateFields, setValidatedDateFields] = useState({ startDate: false, endDate: false });

  const { login, user } = useGoogleSignIn(accessUserCalendar, isEdifier, setCurrentUserGoogleEvents);

  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [originalSlots, setOriginalSlots] = useState([]);
  const [openTimeSlotsDialog, setOpenTimeSlotsDialog] = useState(false);
  const [openDaySlotDialog, setOpenDaySlotDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const [refactoredSlots, setRefactoredSlots] = useState([]);
  const [manualSlots, setManualSlots] = useState({ start: '', end: '', title: '' });

  const [inviteNotificationPayload, setInviteNotificationPayload] = useState({});

  let [refactoredInvites, setRefactoredInvites] = useState({});

  const userDetails = getUserDetails();
  const userToken = userDetails.token;
  const name = searchParams?.get('name');

  const { isFetching: isTimeslotsLoading, data: timeSlotsData } = userQueryGetTimeSlots(
    toast,
    userToken,
    searchParams.get('id') ? searchParams.get('id') : userDetails.id
  );

  const { mutate: modifySlots, isLoading: isModifyingTimeSlots } = useMutationModifyTimeSlots(
    refactoredSlots,
    toast,
    userToken,
    setRefactoredSlots,
    queryClient
  );

  const {
    data,
    isLoading: isNotificationSending,
    mutate: sendMeetingNotification,
  } = useMutationSendInviteNotifications(inviteNotificationPayload, toast, userToken, queryClient);

  const { data: sentInvitesList, isFetching: isLoadingSentInvites } = useQueryGetSentInvites(
    userToken,
    navigate,
    toast
  );

  useEffect(() => {
    sentInvitesList?.data?.data.filter((item) => {
      if (item.userId === searchParams.get('id') && isDateInCurrentWeek(item?.scheduleTime)) {
        refactoredInvites = { ...refactoredInvites, [item?.scheduleTime]: item };
        return item;
      }
    });

    setRefactoredInvites(refactoredInvites);
  }, [sentInvitesList]);

  useEffect(() => {
    setAvailabilitySlots([]);

    // Handle both array response [0] and single object response
    const availabilityData = timeSlotsData?.data?.data;
    const slots = Array.isArray(availabilityData)
      ? availabilityData[0]?.availabilitySlots
      : availabilityData?.availabilitySlots;

    if (slots && slots.length > 0) {
      const formattedSlots = formattedSlotsForCalendar(slots);
      // Add random colors to each slot
      const slotsWithColors = formattedSlots.map((slot) => {
        const color = getRandomColor();
        return {
          ...slot,
          backgroundColor: color.bg,
          borderColor: color.border,
          textColor: '#FFFFFF',
        };
      });
      setAvailabilitySlots(slotsWithColors);
      // Store original slots for comparison
      setOriginalSlots(JSON.parse(JSON.stringify(slotsWithColors)));
    } else {
      // If no slots, reset both
      setAvailabilitySlots([]);
      setOriginalSlots([]);
    }
  }, [timeSlotsData]);

  useEffect(() => {
    toasts
      .filter((t) => t.visible) // Only consider visible toasts
      .filter((_, i) => i >= TOAST_LIMIT) // Is toast index over limit?
      .forEach((t) => toast.dismiss(t.id)); // Dismiss – Use toast.remove(t.id) for no exit animation
  }, [toasts]);

  useEffect(() => {
    if (refactoredSlots?.slots) {
      modifySlots();
    }
  }, [refactoredSlots]);

  useEffect(() => {
    if (Object.keys(inviteNotificationPayload).length > 0) {
      sendMeetingNotification();
    }
  }, [inviteNotificationPayload]);

  // Handle clicking on a day in monthly view
  const handleDateClick = (info) => {
    if (!isEdifier) {
      setSelectedDate(info.date);
      setOpenDaySlotDialog(true);
    }
  };

  // in case of user try adding slot over a slot
  const isSlotOverlap = (newSlot) => {
    // Check if the new slot overlaps with any existing slots
    return availabilitySlots.some(
      (existingSlot) =>
        (newSlot.start >= existingSlot.start && newSlot.start < existingSlot.end) ||
        (newSlot.end > existingSlot.start && newSlot.end <= existingSlot.end) ||
        (newSlot.start <= existingSlot.start && newSlot.end >= existingSlot.end)
    );
  };

  // in case of user try adding slot over a slot

  const handleSelectSlot = ({ start, end }) => {
    // Handle slot selection logic, e.g., add the selected slot to availabilitySlots
    const color = getRandomColor();
    const newSlot = {
      start,
      end,
      title: 'Available',
      backgroundColor: color.bg,
      borderColor: color.border,
      textColor: '#FFFFFF',
    };

    if (!isSlotOverlap(newSlot)) {
      setAvailabilitySlots([...availabilitySlots, newSlot]);
    } else {
      alert('Slot overlaps, Please select a different time.');
    }
  };

  const handleRemoveSlot = (selectedSlot) => {
    // Handle logic to remove the selected slot from availabilitySlots

    const updatedSlots = availabilitySlots.filter((slot) => !isSlotsEqual(slot, selectedSlot));

    setAvailabilitySlots(updatedSlots);
  };

  const isSlotsEqual = (slot1, slot2) => {
    return slot1.start.toString() === slot2.start.toString();
  };

  // Normalize slots for comparison (convert dates to ISO strings)
  const normalizeSlotForComparison = (slot) => {
    return {
      start: new Date(slot.start).toISOString(),
      end: new Date(slot.end).toISOString(),
      title: slot.title || 'Available',
    };
  };

  // Check if there are any changes between current and original slots
  const hasChanges = () => {
    // If lengths are different, there are changes
    if (availabilitySlots.length !== originalSlots.length) {
      return true;
    }

    // If both are empty, no changes
    if (availabilitySlots.length === 0 && originalSlots.length === 0) {
      return false;
    }

    // Normalize both arrays for comparison
    const normalizedCurrent = availabilitySlots.map(normalizeSlotForComparison).sort((a, b) => 
      new Date(a.start) - new Date(b.start)
    );
    const normalizedOriginal = originalSlots.map(normalizeSlotForComparison).sort((a, b) => 
      new Date(a.start) - new Date(b.start)
    );

    // Compare each slot
    for (let i = 0; i < normalizedCurrent.length; i++) {
      const current = normalizedCurrent[i];
      const original = normalizedOriginal[i];

      if (!original || 
          current.start !== original.start || 
          current.end !== original.end ||
          current.title !== original.title) {
        return true;
      }
    }

    return false;
  };

  const saveAvailableTimeSlots = () => {
    const formattedSlots = formatDateTimeSlots(availabilitySlots);

    setRefactoredSlots({ slots: formattedSlots });
  };

  function getDayIndex(day) {
    const daysOrder = days;
    return daysOrder.indexOf(day);
  }

  // manually entered slots through date time input fields
  const handleManualSlots = () => {
    if (!manualSlots.end || !manualSlots.start) {
      toast.error('Start/End Date Required');

      if (!manualSlots.end) {
        validatedDateFields['endDate'] = true;
      }
      if (!manualSlots.start) {
        validatedDateFields['startDate'] = true;
      }
      setValidatedDateFields({ ...validatedDateFields });
      return;
    }

    if (
      new Date(manualSlots.end).getDate() != new Date(manualSlots.start).getDate() ||
      new Date(manualSlots.end).getFullYear() != new Date(manualSlots.start).getFullYear()
    ) {
      toast.error('Date and Year must be the same');

      setValidatedDateFields({ startDate: true, endDate: true });

      return;
    }
    if (new Date(manualSlots.end) < new Date(manualSlots.start)) {
      toast.error('Please select a greater date-time in end time slot');

      setValidatedDateFields({ endDate: true });

      return;
    }
    const color = getRandomColor();
    const newSlot = { 
      start: new Date(manualSlots.start), 
      end: new Date(manualSlots.end), 
      title: manualSlots?.title,
      backgroundColor: color.bg,
      borderColor: color.border,
      textColor: '#FFFFFF',
    };

    const isOverlapping = availabilitySlots.some((slot) => {
      return (
        (newSlot.start >= slot.start && newSlot.start < slot.end) ||
        (newSlot.end > slot.start && newSlot.end <= slot.end) ||
        (newSlot.start <= slot.start && newSlot.end >= slot.end)
      );
    });

    if (isOverlapping) {
      toast.error('Slot overlaps with existing slots. Choose another time.');
      setValidatedDateFields({ startDate: true, endDate: true });

      return;
    }

    toast.success('Slot added to Calendar, Press Save for Updating');
    setAvailabilitySlots([
      ...availabilitySlots,
      { 
        start: new Date(manualSlots.start), 
        end: new Date(manualSlots.end), 
        title: manualSlots.title,
        backgroundColor: color.bg,
        borderColor: color.border,
        textColor: '#FFFFFF',
      },
    ]);

    setManualSlots({ start: '', end: '', title: '' });
  };

  const handleValidation = (manualSlots, value, type) => {
    if (type === 'start') {
      setManualSlots({ ...manualSlots, start: value });

      if (validatedDateFields?.startDate) {
        setValidatedDateFields({ ...validatedDateFields, startDate: false });
      }
    } else {
      setManualSlots({ ...manualSlots, end: value });

      if (validatedDateFields?.endDate) {
        setValidatedDateFields({ ...validatedDateFields, endDate: false });
      }
    }
  };

  // Handle adding time slot for selected day
  const handleAddDaySlot = (startTime, endTime) => {
    if (!selectedDate || !startTime || !endTime) {
      toast.error('Please select both start and end time');
      return;
    }

    // Create date objects for the selected day with the chosen times
    const [startHour, startMinute] = startTime.split(':');
    const [endHour, endMinute] = endTime.split(':');

    const start = new Date(selectedDate);
    start.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

    const end = new Date(selectedDate);
    end.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

    if (end <= start) {
      toast.error('End time must be after start time');
      return;
    }

    const color = getRandomColor();
    const newSlot = {
      start,
      end,
      title: 'Available',
      backgroundColor: color.bg,
      borderColor: color.border,
      textColor: '#FFFFFF',
    };

    if (!isSlotOverlap(newSlot)) {
      setAvailabilitySlots([...availabilitySlots, newSlot]);
      toast.success('Slot added successfully! Remember to save.');
      setOpenDaySlotDialog(false);
      setSelectedDate(null);
    } else {
      toast.error('Slot overlaps with existing slots. Choose another time.');
    }
  };



  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | {isEdifier ? 'User Availability' : 'My Availability'}</title>
      </Helmet>
      {isEdifier && (
        <BreadCrumbs
          breadCrumbs={
            userDetails?.role === 'COACH'
              ? [
                { title: 'Your Teachers', link: '/dashboard/teachers' },
                {
                  title: searchParams.get('name'),
                  link: `/dashboard/user-profile?id=${searchParams.get('id')}&role=edifier`,
                },
                { title: `${searchParams.get('name')}'s Availability` },
              ]
              : [
                { title: 'Find an Edifier', link: '/dashboard/find-edifiers' },
                {
                  title: searchParams.get('name'),
                  link: `/dashboard/user-profile?id=${searchParams.get('id')}&role=teacher`,
                },
                { title: `${searchParams.get('name')}'s Availability` },
              ]
          }
        />
      )}

      <TimeSlotsDialog
        availabilitySlots={availabilitySlots}
        open={openTimeSlotsDialog}
        getDayIndex={getDayIndex}
        handleClose={() => setOpenTimeSlotsDialog(false)}
        setInviteNotificationPayload={setInviteNotificationPayload}
        edifierId={searchParams.get('id')}
        isNotificationSending={isNotificationSending}
        inviteNotificationPayload={inviteNotificationPayload}
        refactoredInvites={refactoredInvites}
        isLoadingSentInvites={isLoadingSentInvites}
        isEdifier={userDetails?.role === 'COACH'}
      />

      <DaySlotSelectionDialog
        open={openDaySlotDialog}
        selectedDate={selectedDate}
        onClose={() => {
          setOpenDaySlotDialog(false);
          setSelectedDate(null);
        }}
        onAddSlot={handleAddDaySlot}
      />

      <Box className="timeslots-calendar timeslots-calendar-google2" sx={{ mt: isEdifier && '30px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row', mb: '20px' } }}>
          <Typography
            variant="h3"
            sx={{
              mb: '5px',
              color: 'text.titles',
              display: 'block',
              textTransform: 'capitalize',
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            {isEdifier ? `${name} ` : 'My '}Availability
          </Typography>
          {isEdifier ? (<Button
            variant="contained"
            color="main"
            sx={{
              height: '40px',
              mt: '20px',
              mb: { xs: '20px', md: '15px' },
              width: 'fit-content',
              mx: { xs: 'auto', md: '0' },
            }}
            onClick={() => setOpenTimeSlotsDialog(true)}
          >
            Schedule Meeting
          </Button>) : (
            <Box
              sx={{
                textAlign: 'right',
                mb: '26px',
                display: 'flex',
                columnGap: '12px',
                justifyContent: 'right',
                height: '35px',
              }}
            >

              <LoadingButton
                variant="contained"
                loading={isModifyingTimeSlots}
                color="main"
                onClick={() => saveAvailableTimeSlots()}
                disabled={!hasChanges() || isModifyingTimeSlots}
                sx={{ width: '118px' }}
              >
                Save Slot
              </LoadingButton>
            </Box>
          )}

          
        </Box>


        {isTimeslotsLoading && (
          <Box sx={{ textAlign: 'center', position: 'relative', top: '200px' }}>
            <CircularProgress />
          </Box>
        )}
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          selectable={false}
          dateClick={handleDateClick}
          ref={calendarRef}
          events={availabilitySlots}
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: '',
          }}
          eventDisplay="block"
          displayEventTime={true}
          displayEventEnd={true}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          eventContent={(eventContent) => {
            const view = eventContent.view.type;
            const isMonthView = view === 'dayGridMonth';

            return (
              <div style={{ padding: '10px', position: 'relative' }}>
                <strong>{eventContent.timeText}</strong>
                {!isEdifier && (
                  <button
                    style={{ position: 'absolute', right: '5px', top: '2px', background: 'none', border: 'none', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => handleRemoveSlot(eventContent.event)}
                  >
                    <CloseIcon sx={{ cursor: 'pointer', color: '#FFFFFF', fontSize: '18px' }} />
                  </button>
                )}
              </div>
            );
          }}
          dayCellClassNames={(arg) => {
            return !isEdifier ? 'clickable-day' : '';
          }}
          dayMaxEvents={3}
          moreLinkText="more"
        />
      </Box>
    </Box>
  );
};

// Dialog component for selecting time slots on a specific day
const DaySlotSelectionDialog = ({ open, selectedDate, onClose, onAddSlot }) => {
  // Get current time and set default times
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const getEndTime = () => {
    const now = new Date();
    const endTime = new Date(now.getTime() + 30 * 60000); // Add 30 minutes
    const hours = endTime.getHours().toString().padStart(2, '0');
    const minutes = endTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const [startTime, setStartTime] = useState(getCurrentTime());
  const [endTime, setEndTime] = useState(getEndTime());

  // Reset to current time when dialog opens
  useEffect(() => {
    if (open) {
      setStartTime(getCurrentTime());
      setEndTime(getEndTime());
    }
  }, [open]);

  const handleAdd = () => {
    onAddSlot(startTime, endTime);
    setStartTime(getCurrentTime());
    setEndTime(getEndTime());
  };

  const formatDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1299,
          display: open ? 'block' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: open ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) scale(0.95)',
          backgroundColor: 'background.paper',
          borderRadius: '8px',
          boxShadow: 24,
          p: 4,
          minWidth: '400px',
          maxWidth: '500px',
          zIndex: 1300,
          border: 'none',
          opacity: open ? 1 : 0,
          transition: 'all 0.3s ease',
          visibility: open ? 'visible' : 'hidden',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
          Add Time Slot
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          {formatDate(selectedDate)}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" color="primary">
            Add Slot
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AvailabilityCalendar;
